import { Fragment, useEffect, useMemo, useState } from "react";

import { AuthStatus, UnitLabelType } from "@asd/customer-portal-api";
import { formatDashedDate, formatDate, formatShortDate, parseWallClockDate, useOrganization } from "@asd/customer-portal-ui";
import RestoreIcon from "@mui/icons-material/Restore";
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { BarChart, ChartsReferenceLine } from "@mui/x-charts";
import { SeriesValueFormatter } from "@mui/x-charts/internals";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment, { Moment } from "moment-timezone";
import { useSelector } from "react-redux";

import FilterDatePickerTextField from "~/components/shared/FilterDatePickerTextField";
import { useScrambler } from "~/providers/ScramblerProvider";
import { isAdditionalEmployerExpense, UNIT_USAGE_OPTIONS } from "~/utils/unitUsageHelpers";
import { DashboardPPTFilters } from "./DashboardPPT";
import { getChartDatasets, NormalizedAuthorization } from "./dashboardPPTHelpers";
import TotalUsageBar from "./TotalUsageBar";
import TotalUsageBarLabel from "./TotalUsageBarLabel";
import TotalUsageLegend from "./TotalUsageLegend";
import TotalUsageTooltip from "./TotalUsageTooltip";
import UsageOverTimeLegend from "./UsageOverTimeLegend";
import UsageOverTimeTooltip from "./UsageOverTimeTooltip";

export type TotalUsageType = {
  authorizationServiceId: string;
  allocatedDollars: number;
  allocatedUnits: number;
  adjustedAuthorizedTotal: number;
  adjustedUnitTotal: number;
  authUsagePercentage: number;
  unitUsagePercentage: number;
  forecast: number;
  claimSum: number;
  usedUnits: number;
  label: string;
};

export type ServiceDateGroups = {
  groupedBy: "day" | "week" | "month";
  dateGroups: Moment[] | Moment[][];
};

export type UsageOverTimeDatasetType = {
  [key: string]: any;
  dateGroup?: number | Array<number>;
};

export type UsageOverTime = {
  serviceDateGroups: ServiceDateGroups;
  dataset: Array<UsageOverTimeDatasetType>;
  series: {
    dataKey: string;
    label: string;
    valueFormatter: SeriesValueFormatter<number | null>;
  } | null;
  forecast: {
    actual: number;
    adjusted: number;
  };
};

export type ChartDatasetsType = {
  totalUsage: TotalUsageType;
  usageOverTime: UsageOverTime;
};

const DEFAULT_CHART_DATASETS = {
  totalUsage: {
    authorizationServiceId: "",
    allocatedDollars: 0,
    allocatedUnits: 0,
    adjustedAuthorizedTotal: 0,
    adjustedUnitTotal: 0,
    authUsagePercentage: 0,
    unitUsagePercentage: 0,
    forecast: 0,
    claimSum: 0,
    usedUnits: 0,
    label: "",
  },
  usageOverTime: {
    serviceDateGroups: {
      groupedBy: "day" as const,
      dateGroups: [],
    },
    dataset: [],
    series: null,
    forecast: {
      actual: 0,
      adjusted: 0,
    },
  },
};

const DashboardPPTCharts = ({
  authorization,
  filters,
  setFilters,
}: {
  authorization: NormalizedAuthorization | null;
  filters: DashboardPPTFilters;
  setFilters: (_action: any) => void;
}) => {
  const theme = useTheme();
  const {
    configuration: {
      ppt: { showExpectedUsageForInactiveAuthorizations },
    },
  } = useOrganization();

  const { isScramblerEnabled } = useScrambler();

  const lastPayDate = useSelector((state: { ConfigurationSlice: { lastPayDate?: string } }) => state.ConfigurationSlice.lastPayDate);

  const [chartDatasets, setChartDatasets] = useState<ChartDatasetsType>(DEFAULT_CHART_DATASETS);
  // const [highlightedAuth, setHighLightedAuth] = useState<HighlightItemData | null>(null);
  const [isAuthorizationChanging, setIsAuthorizationChanging] = useState(false);

  const hideExpectedUsage = useMemo(() => {
    return (
      isAdditionalEmployerExpense(authorization?.description) ||
      (authorization?.status !== AuthStatus.Active && !showExpectedUsageForInactiveAuthorizations)
    );
  }, [authorization, showExpectedUsageForInactiveAuthorizations]);

  const isAuthorizationInDollars = authorization?.unitLabel === UnitLabelType.Dollars;

  useEffect(() => {
    setIsAuthorizationChanging(true);
    setTimeout(() => {
      setIsAuthorizationChanging(false);
    });
  }, [authorization]);

  useEffect(() => {
    if (!authorization) {
      setChartDatasets(DEFAULT_CHART_DATASETS);
    } else if (!Object.keys(filters.errors).length) {
      setChartDatasets(getChartDatasets(authorization, filters.startDate!, filters.endDate!, isScramblerEnabled));
    }
  }, [authorization, filters.errors, filters.startDate, filters.endDate, isScramblerEnabled]);

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          alignContent: "center",
          textAlign: "center",
          minWidth: `calc((${theme.spacing(4)} * 2) + 200px)`,
        }}
      >
        <Stack direction="column" height="100%" p={1}>
          <Stack alignItems="center" direction="column" justifyContent="space-between" sx={{ justifyContent: "center" }}>
            <Typography color="custom.main" fontWeight="bold" variant="h5">
              Total Service Usage
            </Typography>
            <Typography fontWeight="bold" sx={{ fontStyle: "italic" }} variant="body1">
              ({formatDate(authorization?.startDate)} - {formatDate(authorization?.stopDate)})
            </Typography>
          </Stack>
          <Divider flexItem />
          <Stack alignItems="center" direction="row" flexGrow={1} gap={1} mt={2}>
            <Box alignItems="center" display="grid" gap={1} gridTemplateColumns="auto 1fr" textAlign="start">
              {UNIT_USAGE_OPTIONS.map(({ id, label, backgroundColor }) => (
                <Fragment key={id}>
                  <Box borderRadius={2} height={theme.spacing(3)} sx={{ backgroundColor }} width={theme.spacing(3)} />
                  <Typography>{label}</Typography>
                </Fragment>
              ))}
            </Box>
            <Divider orientation="vertical" sx={{ borderRightWidth: 2 }} flexItem />
            <BarChart
              barLabel="value"
              borderRadius={5}
              dataset={[chartDatasets.totalUsage]}
              grid={{ vertical: true }}
              height={250}
              layout="horizontal"
              margin={{ left: -10 }}
              // onHighlightChange={(item) => {
              //   let newItem = null;

              //   const highlightAuth = chartDatasets.totalUsage;
              //   if (highlightAuth && item?.dataIndex != null) {
              //     newItem = {
              //       dataIndex: item.dataIndex,
              //       seriesId: highlightAuth.authorizationServiceId,
              //     };
              //   }
              //   setHighLightedAuth(newItem);
              // }}
              series={[
                {
                  id: "usedUnits",
                  dataKey: "usedUnits",
                  label: `${authorization ? `${authorization?.unitLabel}s` : "Units"} Used`,
                  stack: "auth",
                  color: theme.palette.primary.main,
                  valueFormatter: (value) => `${value ?? 0}`,
                },
                {
                  id: "adjustedUnitTotal",
                  dataKey: "adjustedUnitTotal",
                  label: `Total Approved ${authorization ? `${authorization?.unitLabel}s` : "Units"}`,
                  stack: "auth",
                  color: `url(#Pattern)`,
                  valueFormatter: () => `${chartDatasets.totalUsage?.allocatedUnits ?? 0}`,
                },
              ]}
              slotProps={{
                bar: {
                  // @ts-ignore
                  totalUsage: chartDatasets.totalUsage,
                  // highlightedAuth,
                },
                barLabel: {
                  // @ts-ignore
                  unitLabel: authorization?.unitLabel,
                  totalUsage: chartDatasets.totalUsage,
                },
                legend: {
                  // @ts-ignore
                  hideExpectedUsage,
                  totalUsage: chartDatasets.totalUsage,
                },
                tooltip: {
                  // @ts-ignore
                  hideExpectedUsage,
                  unitLabel: authorization?.unitLabel,
                  totalUsage: chartDatasets.totalUsage,
                },
              }}
              slots={{
                // @ts-ignore
                bar: TotalUsageBar,
                // @ts-ignore
                barLabel: TotalUsageBarLabel,
                // @ts-ignore
                legend: TotalUsageLegend,
                // @ts-ignore
                tooltip: TotalUsageTooltip,
              }}
              sx={{
                "& .MuiBarLabel-root": {
                  fill: theme.palette.common.white,
                  textAnchor: "middle",
                  dominantBaseline: "central",
                  pointerEvents: "none",

                  "&.MuiBarLabel-series-usedUnits, &.MuiBarLabel-series-adjustedUnitTotal": {
                    ...(theme.palette.mode === "light" && {
                      filter: "url(#LabelBackground)",
                      fill: theme.palette.common.black,
                      fontWeight: "bold",
                    }),
                  },
                },
              }}
              tooltip={{ trigger: "axis" }}
              xAxis={[
                {
                  tickLabelMinGap: 20,
                  height: 30,
                  tickLabelStyle: {
                    fontSize: 14,
                  },
                  valueFormatter: (value: string) => `${isAuthorizationInDollars ? "$" : ""}${value ?? 0}`,
                },
              ]}
              yAxis={[
                {
                  scaleType: "band",
                  dataKey: "label",
                  width: 0,
                  valueFormatter: (label, { location }) => {
                    if (!label || location !== "tooltip") {
                      return "";
                    }

                    // const { text, percentage } = JSON.parse(label);
                    // return location !== "tooltip" ? `${text}` : `${text}: ${+percentage?.toFixed(2) || 0}% Used`;

                    const { percentage } = JSON.parse(label);
                    return `${+percentage?.toFixed(2) || 0}% Used (as of ${lastPayDate ? formatDashedDate(parseWallClockDate(lastPayDate)) : ""})`;
                  },
                },
              ]}
            >
              {!hideExpectedUsage && chartDatasets.totalUsage?.allocatedUnits > 1 && chartDatasets.totalUsage?.forecast && (
                <ChartsReferenceLine
                  labelAlign="end"
                  lineStyle={{ stroke: theme.palette.error.main, strokeWidth: 2, zIndex: -1 }}
                  x={chartDatasets.totalUsage.forecast}
                />
              )}
              <pattern height="7" id="Pattern" patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="7">
                <line stroke={theme.palette.primary.dark} strokeWidth="2" x1="0" x2="0" y="0" y2="7" />
                <rect fill={theme.palette.primary.dark} fillOpacity="0.2" height="100%" width="100%" />
              </pattern>
              <pattern height="7" id="PatternHighlight" patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="7">
                <line filter="brightness(150%)" stroke={theme.palette.primary.dark} strokeWidth="2" x1="0" x2="0" y="0" y2="7" />
                <rect fill={theme.palette.primary.dark} fillOpacity="0.2" filter="brightness(150%)" height="100%" width="100%" />
              </pattern>
              <pattern height="7" id="PatternFade" patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="7">
                <line opacity="0.4" stroke={theme.palette.primary.dark} strokeWidth="2" x1="0" x2="0" y="0" y2="7" />
                <rect fill={theme.palette.primary.dark} fillOpacity="0.1" height="100%" width="100%" />
              </pattern>
              <filter height="110%" id="LabelBackground" width="105%" x="-2.5%" y="-2.5%">
                <feFlood floodColor={theme.palette.common.white} />
                <feGaussianBlur stdDeviation="2" />
                <feComponentTransfer>
                  <feFuncA tableValues="0 0 0 1" type="table" />
                </feComponentTransfer>

                <feComponentTransfer>
                  <feFuncA tableValues="0 1 1 1 1 1 1 1" type="table" />
                </feComponentTransfer>
                <feComposite in="SourceGraphic" operator="over" />
              </filter>
            </BarChart>
          </Stack>
        </Stack>
      </Paper>
      <Divider />
      <Stack
        direction="row"
        sx={{
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Tooltip sx={{ alignContent: "center", height: "fit-content" }} title="Reset Date Filters">
          <IconButton
            onClick={() => {
              setFilters({
                key: "startDate",
                value: moment(authorization?.startDate),
              });
              setFilters({
                key: "endDate",
                value: moment(authorization?.stopDate),
              });
            }}
            size="small"
          >
            <RestoreIcon />
          </IconButton>
        </Tooltip>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <FilterDatePickerTextField
            filterKey="startDate"
            filters={filters}
            label="Service Start Date"
            maxDate={filters.endDate ?? authorization?.stopDate ?? null}
            minDate={authorization?.startDate ?? null}
            onError={(newError: string) => {
              let errorMessage = null;

              if (newError) {
                errorMessage = "Invalid Date";

                if (newError === "maxDate") {
                  errorMessage = "Min Date cannot exceed Max Date";
                } else if (newError === "minDate") {
                  errorMessage = `No Data prior to ${formatDate(authorization?.startDate)}`;
                }
              }

              setFilters({
                key: "errors",
                value: {
                  key: "startDate",
                  value: errorMessage,
                },
              });
            }}
            setFilters={setFilters}
            slotProps={{
              textField: {
                helperText: filters.errors.startDate,
              },
            }}
            sx={{ flexGrow: 1 }}
            value={filters.startDate}
          />
          <Typography sx={{ alignContent: "center", height: "fit-content" }}>-</Typography>
          <FilterDatePickerTextField
            filterKey="endDate"
            filters={filters}
            label="Service End Date"
            maxDate={authorization?.stopDate ?? null}
            minDate={filters.startDate ?? authorization?.startDate ?? null}
            onError={(newError: string) => {
              let errorMessage = null;

              if (newError) {
                errorMessage = "Invalid Date";

                if (newError === "minDate") {
                  errorMessage = "Max Date cannot precede Min Date";
                } else if (newError === "maxDate") {
                  errorMessage = `No Data after ${formatDate(authorization?.stopDate)}`;
                }
              }

              setFilters({
                key: "errors",
                value: {
                  key: "endDate",
                  value: errorMessage,
                },
              });
            }}
            setFilters={setFilters}
            slotProps={{
              textField: {
                helperText: filters.errors.endDate,
              },
            }}
            sx={{ flexGrow: 1 }}
            value={filters.endDate}
          />
        </LocalizationProvider>
      </Stack>
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          alignContent: "center",
          textAlign: "center",
          minWidth: `calc((${theme.spacing(4)} * 2) + 200px)`,
        }}
      >
        <Stack direction="column" height="100%" p={1}>
          <Stack alignItems="center" direction="column" justifyContent="space-between" sx={{ justifyContent: "center" }}>
            <Typography color="custom.main" fontWeight="bold" variant="h5">
              Detailed Usage
            </Typography>
            <Typography fontWeight="bold" sx={{ fontStyle: "italic" }} variant="body1">
              Use the date fields above to change the displayed timeframe
            </Typography>
          </Stack>
          <Divider flexItem />
          <BarChart
            borderRadius={5}
            dataset={chartDatasets.usageOverTime.dataset}
            grid={{ horizontal: true }}
            height={300}
            // highlightedItem={highlightedAuth}
            margin={{ left: 0 }}
            // onHighlightChange={setHighLightedAuth}
            onItemClick={(_event, { dataIndex }) => {
              const { dateGroup } = chartDatasets.usageOverTime.dataset[dataIndex!];

              if (chartDatasets.usageOverTime.serviceDateGroups.groupedBy === "day") {
                setFilters({
                  key: "startDate",
                  value: moment(dateGroup),
                });
                setFilters({
                  key: "endDate",
                  value: moment(dateGroup).endOf("day"),
                });
              } else if (chartDatasets.usageOverTime.serviceDateGroups.groupedBy === "week") {
                const dateGroupArray = dateGroup as Array<number>;

                setFilters({
                  key: "startDate",
                  value: moment(dateGroupArray[0]),
                });
                setFilters({
                  key: "endDate",
                  value: moment(dateGroupArray[1]),
                });
              } else {
                setFilters({
                  key: "startDate",
                  value: moment(dateGroup).startOf("month"),
                });
                setFilters({
                  key: "endDate",
                  value: moment(dateGroup).endOf("month"),
                });
              }
            }}
            series={chartDatasets.usageOverTime.series ? [{ ...chartDatasets.usageOverTime.series, color: theme.palette.custom.main }] : []}
            slotProps={{
              legend: {
                // @ts-ignore
                hideExpectedUsage,
                unitLabel: authorization?.unitLabel,
              },
              tooltip: {
                trigger: "item",
                // @ts-ignore
                hideExpectedUsage,
                unitLabel: authorization?.unitLabel,
                usageOverTime: chartDatasets.usageOverTime,
              },
            }}
            slots={{
              // @ts-ignore
              legend: UsageOverTimeLegend,
              // @ts-ignore
              tooltip: UsageOverTimeTooltip,
            }}
            sx={{
              mt: 1,
            }}
            xAxis={[
              {
                dataKey: "dateGroup",
                height: 30,
                tickLabelStyle: {
                  fontSize: 14,
                },
                valueFormatter: (value: string) => {
                  if (!value) {
                    return "";
                  }

                  if (chartDatasets.usageOverTime.serviceDateGroups.groupedBy === "day") {
                    return formatShortDate(moment(value));
                  } else if (chartDatasets.usageOverTime.serviceDateGroups.groupedBy === "week") {
                    return `${formatShortDate(moment(value[0]))}-${formatShortDate(moment(value[1]))}`;
                  } else {
                    const valueDate = moment(value);
                    return `${valueDate.format("MMM")} '${valueDate.format("YY")}`;
                  }
                },
              },
            ]}
            yAxis={[
              {
                width: 60,
                tickLabelStyle: {
                  fontSize: 14,
                },
                valueFormatter: (value: string) => `${isAuthorizationInDollars ? "$" : ""}${value ?? 0}`,
              },
            ]}
          >
            {!hideExpectedUsage &&
              !isAuthorizationChanging &&
              chartDatasets.totalUsage?.allocatedUnits > 1 &&
              chartDatasets.usageOverTime?.forecast?.adjusted && (
                <ChartsReferenceLine
                  lineStyle={{ stroke: theme.palette.error.main, strokeWidth: 2 }}
                  y={
                    chartDatasets.usageOverTime.forecast.adjusted +
                    (chartDatasets.usageOverTime.forecast.actual === chartDatasets.usageOverTime.forecast.adjusted
                      ? 0
                      : chartDatasets.usageOverTime.forecast.adjusted * 0.05)
                  }
                />
              )}
          </BarChart>
        </Stack>
      </Paper>
    </>
  );
};

export default DashboardPPTCharts;
